/**
 * 员工明细列表页
 *
 * 品牌筛选 + 标签筛选 + 分页 DataGrid，展示每个品牌下的员工登录明细。
 * 支持勾选批量操作：批量删除、批量标记正式/测试，以及行内删除。
 * 数据来源：GET /admin/employees、PATCH /admin/employees/{id}、POST /admin/employees/batch-delete
 */

import { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
  Alert,
  Button,
  Stack,
} from '@mui/material'
import type { AlertColor } from '@mui/material'
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid'
import { AdminLayout } from '@/components/AdminLayout'
import {
  getEmployees,
  getBrands,
  updateEmployee,
  batchDeleteEmployees,
  type EmployeeItem,
} from '@/api/services'
import type { Brand } from '@chaoyinli/shared'
import { formatDate } from '@/utils/format'

/** 员工状态颜色映射 */
const STATUS_COLORS: Record<string, string> = {
  active: '#4CAF50',
  disabled: '#F44336',
}

/** 标签颜色映射 */
const TAG_COLORS: Record<string, string> = {
  official: '#4CAF50',
  test: '#FF8C00',
}

/** 标签中文映射 */
const TAG_LABELS: Record<string, string> = {
  official: '正式',
  test: '测试',
}

export function EmployeesPage() {
  const [items, setItems] = useState<EmployeeItem[]>([])
  const [brands, setBrands] = useState<Brand[]>([])
  const [brandFilter, setBrandFilter] = useState('')
  const [tagFilter, setTagFilter] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [actionLoading, setActionLoading] = useState(false)
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([])

  // Toast 状态
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: AlertColor }>({
    open: false,
    message: '',
    severity: 'info',
  })

  const showToast = (message: string, severity: AlertColor = 'info') => {
    setToast({ open: true, message, severity })
  }

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }))
  }

  // 加载品牌下拉
  useEffect(() => {
    getBrands()
      .then(setBrands)
      .catch(() => setBrands([]))
  }, [])

  const fetchEmployees = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await getEmployees({
        brand_id: brandFilter || undefined,
        tag: tagFilter || undefined,
        page: 1,
        page_size: 50,
      })
      setItems(data.items)
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载员工列表失败')
    } finally {
      setLoading(false)
    }
  }, [brandFilter, tagFilter])

  useEffect(() => {
    fetchEmployees()
  }, [fetchEmployees])

  const selectedIds = Array.from(rowSelectionModel).map(String)
  const hasSelection = selectedIds.length > 0

  /** 批量删除 */
  const handleBatchDelete = () => {
    if (!hasSelection || actionLoading) return
    if (!window.confirm(`确定删除选中的 ${selectedIds.length} 个员工？此操作不可恢复`)) return
    setActionLoading(true)
    batchDeleteEmployees(selectedIds)
      .then((res) => {
        showToast(`已删除 ${res.deleted} 个员工`, 'success')
        setRowSelectionModel([])
        fetchEmployees()
      })
      .catch((e) => showToast(e instanceof Error ? e.message : '删除失败', 'error'))
      .finally(() => setActionLoading(false))
  }

  /** 批量标记标签 */
  const handleMarkTag = (tag: 'test' | 'official') => {
    if (!hasSelection || actionLoading) return
    setActionLoading(true)
    Promise.all(selectedIds.map((id) => updateEmployee(id, { tag })))
      .then(() => {
        showToast(
          `已将 ${selectedIds.length} 个员工标记为${TAG_LABELS[tag] ?? tag}`,
          'success',
        )
        setRowSelectionModel([])
        fetchEmployees()
      })
      .catch((e) => showToast(e instanceof Error ? e.message : '操作失败', 'error'))
      .finally(() => setActionLoading(false))
  }

  /** 行内删除 */
  const handleRowDelete = (id: string) => {
    if (actionLoading) return
    if (!window.confirm('确定删除该员工？此操作不可恢复')) return
    setActionLoading(true)
    batchDeleteEmployees([id])
      .then((res) => {
        showToast(`已删除 ${res.deleted} 个员工`, 'success')
        fetchEmployees()
      })
      .catch((e) => showToast(e instanceof Error ? e.message : '删除失败', 'error'))
      .finally(() => setActionLoading(false))
  }

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: '员工姓名',
      width: 140,
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'brand_name',
      headerName: '所属品牌',
      width: 180,
      valueGetter: (v) => v || '-',
    },
    {
      field: 'tag',
      headerName: '标签',
      width: 110,
      renderCell: (params) => {
        const value = params.value as string
        const color = TAG_COLORS[value] || '#999'
        const label = TAG_LABELS[value] || '-'
        return (
          <Chip
            label={label}
            size="small"
            sx={{
              backgroundColor: `${color}15`,
              color,
              fontWeight: 600,
              fontSize: '12px',
            }}
          />
        )
      },
    },
    {
      field: 'status',
      headerName: '状态',
      width: 110,
      renderCell: (params) => {
        const color = STATUS_COLORS[params.value] || '#999'
        return (
          <Chip
            label={params.value === 'active' ? '正常' : '已禁用'}
            size="small"
            sx={{
              backgroundColor: `${color}15`,
              color,
              fontWeight: 600,
              fontSize: '12px',
            }}
          />
        )
      },
    },
    {
      field: 'last_active_at',
      headerName: '最后活跃时间',
      width: 200,
      valueGetter: (v) => (v ? formatDate(v) : '从未活跃'),
    },
    {
      field: 'actions',
      headerName: '操作',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          size="small"
          color="error"
          sx={{ fontSize: '12px' }}
          onClick={() => handleRowDelete(params.row.id)}
        >
          删除
        </Button>
      ),
    },
  ]

  return (
    <AdminLayout>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
          员工明细
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>筛选品牌</InputLabel>
            <Select
              label="筛选品牌"
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
            >
              <MenuItem value="">全部品牌</MenuItem>
              {brands.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>筛选标签</InputLabel>
            <Select
              label="筛选标签"
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
            >
              <MenuItem value="">全部</MenuItem>
              <MenuItem value="official">正式</MenuItem>
              <MenuItem value="test">测试</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            color="error"
            size="small"
            disabled={!hasSelection || actionLoading}
            onClick={handleBatchDelete}
          >
            批量删除
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={!hasSelection || actionLoading}
            onClick={() => handleMarkTag('test')}
            sx={{ color: '#FF8C00', borderColor: '#FF8C00' }}
          >
            标记为测试
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={!hasSelection || actionLoading}
            onClick={() => handleMarkTag('official')}
            sx={{ color: '#4CAF50', borderColor: '#4CAF50' }}
          >
            标记为正式
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          height: 'calc(100vh - 220px)',
          minHeight: 400,
          backgroundColor: '#fff',
          borderRadius: '12px',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#FF8C00' }} />
          </Box>
        ) : (
          <DataGrid
            rows={items}
            columns={columns}
            getRowId={(row) => row.id}
            checkboxSelection
            disableRowSelectionOnClick
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={setRowSelectionModel}
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell:focus': { outline: 'none' },
              '& .MuiDataGrid-columnHeader': { backgroundColor: '#fafafa' },
            }}
          />
        )}
      </Box>

      {/* Toast */}
      <Alert
        severity={toast.severity}
        variant="filled"
        sx={{
          position: 'fixed',
          top: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          display: toast.open ? 'flex' : 'none',
          zIndex: 2000,
        }}
        onClose={handleCloseToast}
      >
        {toast.message}
      </Alert>
    </AdminLayout>
  )
}

export default EmployeesPage
